import { PLACE_DETAIL_FIELDS, PlaceDetails, normalizePlaceDetails } from './placeDetails';

export interface LocationRecommendation {
  name: string;
  place_id: string;
}

export const normalizePlaceId = (placeId?: string | null): string => {
  if (!placeId) return '';
  const trimmed = String(placeId).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('places/')) return trimmed.slice(7);
  return trimmed;
};

export const normalizeLocationRecommendations = (
  recommendations?: Array<Record<string, unknown>> | null,
): LocationRecommendation[] => {
  if (!Array.isArray(recommendations)) return [];

  return recommendations
    .map((item) => ({
      name: String(item.name || item.title || '').trim(),
      place_id: normalizePlaceId(
        (item.place_id || item.placeId || item.google_place_id) as string | undefined,
      ),
    }))
    .filter((item) => item.name);
};

const looksLikeGooglePlaceId = (placeId: string) =>
  /^[A-Za-z0-9_-]{10,}$/.test(placeId) && !/\s/.test(placeId);

const NEW_PLACE_FIELDS = [
  'displayName',
  'formattedAddress',
  'location',
  'rating',
  'userRatingCount',
  'nationalPhoneNumber',
  'internationalPhoneNumber',
  'websiteURI',
  'googleMapsURI',
  'regularOpeningHours',
  'photos',
  'priceLevel',
  'types',
  'businessStatus',
  'editorialSummary',
];

const mapNewPlaceToDetails = (place: Record<string, any>): PlaceDetails => {
  const photos = (place.photos || []).map((photo: Record<string, any>) => ({
    getUrl: (options: { maxWidth?: number; maxHeight?: number }) =>
      photo.getURI?.({ maxWidth: options.maxWidth, maxHeight: options.maxHeight }) || '',
  }));

  const location = place.location;
  const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
  const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;

  return normalizePlaceDetails({
    name: place.displayName?.text || place.displayName || '',
    formatted_address: place.formattedAddress,
    rating: place.rating,
    user_ratings_total: place.userRatingCount,
    formatted_phone_number: place.nationalPhoneNumber,
    international_phone_number: place.internationalPhoneNumber,
    website: place.websiteURI,
    url: place.googleMapsURI,
    price_level: place.priceLevel,
    types: place.types,
    business_status: place.businessStatus,
    editorial_summary: place.editorialSummary?.text
      ? { overview: place.editorialSummary.text }
      : place.editorialSummary,
    opening_hours: place.regularOpeningHours
      ? {
          open_now: place.regularOpeningHours.openNow,
          weekday_text: place.regularOpeningHours.weekdayDescriptions,
        }
      : undefined,
    photos,
    geometry: lat !== undefined && lng !== undefined
      ? {
          location: {
            lat: () => lat,
            lng: () => lng,
          },
        }
      : undefined,
  });
};

const resolvePlaceIdFromQuery = (
  mapElement: HTMLElement,
  query: string,
  locationBias?: { lat: number; lng: number },
): Promise<string | null> =>
  new Promise((resolve) => {
    if (!window.google?.maps?.places) {
      resolve(null);
      return;
    }

    const service = new window.google.maps.places.PlacesService(mapElement);
    service.findPlaceFromQuery(
      {
        query,
        fields: ['place_id', 'name'],
        locationBias: locationBias
          ? {
              center: locationBias,
              radius: 50000,
            }
          : undefined,
      },
      (results: Array<{ place_id?: string }> | null, status: string) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results?.[0]?.place_id) {
          resolve(null);
          return;
        }
        resolve(results[0].place_id!);
      },
    );
  });

const fetchWithLegacyPlacesService = (
  mapElement: HTMLElement,
  placeId: string,
): Promise<PlaceDetails | null> =>
  new Promise((resolve) => {
    if (!window.google?.maps?.places) {
      resolve(null);
      return;
    }

    const service = new window.google.maps.places.PlacesService(mapElement);
    service.getDetails(
      {
        placeId,
        fields: PLACE_DETAIL_FIELDS,
      },
      (place: PlaceDetails | null, status: string) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          resolve(null);
          return;
        }

        resolve(
          normalizePlaceDetails(
            place as PlaceDetails & { current_opening_hours?: PlaceDetails['opening_hours'] },
          ),
        );
      },
    );
  });

const fetchWithNewPlacesApi = async (placeId: string): Promise<PlaceDetails | null> => {
  if (!window.google?.maps?.importLibrary) return null;

  try {
    const placesLibrary = await window.google.maps.importLibrary('places') as {
      Place: new (options: { id: string }) => {
        fetchFields: (options: { fields: string[] }) => Promise<void>;
      } & Record<string, any>;
    };
    const place = new placesLibrary.Place({ id: placeId });
    await place.fetchFields({ fields: NEW_PLACE_FIELDS });
    const details = mapNewPlaceToDetails(place as unknown as Record<string, any>);
    return details.name ? details : null;
  } catch {
    return null;
  }
};

export const fetchPlaceDetailsFromGoogle = async (
  mapElement: HTMLElement | null,
  placeId: string,
  options?: {
    queryFallback?: string;
    locationBias?: { lat: number; lng: number };
  },
): Promise<PlaceDetails | null> => {
  const normalizedId = normalizePlaceId(placeId);
  const candidateId = looksLikeGooglePlaceId(normalizedId) ? normalizedId : '';

  if (candidateId) {
    const fromNewApi = await fetchWithNewPlacesApi(candidateId);
    if (fromNewApi) return fromNewApi;

    if (mapElement) {
      const fromLegacyApi = await fetchWithLegacyPlacesService(mapElement, candidateId);
      if (fromLegacyApi) return fromLegacyApi;
    }
  }

  const query = options?.queryFallback?.trim();
  if (!query || !mapElement) return null;

  const resolvedId = await resolvePlaceIdFromQuery(mapElement, query, options?.locationBias);
  if (!resolvedId) return null;

  return fetchPlaceDetailsFromGoogle(mapElement, resolvedId, options);
};

export const fetchPlaceGeometryFromGoogle = async (
  mapElement: HTMLElement | null,
  placeId: string,
  options?: {
    queryFallback?: string;
    locationBias?: { lat: number; lng: number };
  },
): Promise<{ lat: number; lng: number; resolvedPlaceId: string } | null> => {
  const normalizedId = normalizePlaceId(placeId);
  const candidateId = looksLikeGooglePlaceId(normalizedId) ? normalizedId : '';

  if (candidateId && mapElement) {
    const fromNewApi = await fetchWithNewPlacesApi(candidateId);
    const location = fromNewApi ? getPlaceLocation(fromNewApi) : null;
    if (location) {
      return { ...location, resolvedPlaceId: candidateId };
    }

    const fromLegacyApi = await fetchWithLegacyPlacesService(mapElement, candidateId);
    const legacyLocation = fromLegacyApi ? getPlaceLocation(fromLegacyApi) : null;
    if (legacyLocation) {
      return { ...legacyLocation, resolvedPlaceId: candidateId };
    }
  }

  const query = options?.queryFallback?.trim();
  if (!query || !mapElement) return null;

  const resolvedId = await resolvePlaceIdFromQuery(mapElement, query, options?.locationBias);
  if (!resolvedId) return null;

  const details = await fetchPlaceDetailsFromGoogle(mapElement, resolvedId, options);
  const resolvedLocation = details ? getPlaceLocation(details) : null;
  if (!resolvedLocation) return null;

  return {
    ...resolvedLocation,
    resolvedPlaceId: resolvedId,
  };
};

export const getPlaceLocation = (place: PlaceDetails): { lat: number; lng: number } | null => {
  const location = place.geometry?.location;
  if (!location) return null;
  return { lat: location.lat(), lng: location.lng() };
};
